import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { StatCard } from "@/components/data-display/stat-card";
import { PageHeader } from "@/components/data-display/page-header";
import { InfoBanner } from "@/components/ui/banner";
import { Input, Field } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import {
  LIQ_VENUES,
  LIQ_DASHBOARD_METRICS,
  LIQ_RECENT_QUOTES,
  LIQ_DISPOSITIONS,
  LIQ_QUOTE_0891,
  LIQ_RFQ_ROUTES,
  LIQ_INTENT_REVIEW,
  LIQ_ORDER_7823,
} from "./treasury-data";
import { BackLink, ToneBadge, PositionStatus, TreasuryBanner } from "./treasury-ui";
import type { TreasuryTone } from "./treasury-data";

const deferTo = (frame: string, label: string) =>
  toast({
    variant: "info",
    title: `${label} — not implemented`,
    description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
  });

const DISPOSITION_TONE: Record<string, TreasuryTone> = {
  "quote-expired": "danger",
  "order-placed": "info",
  "partially-filled": "info",
  filled: "success",
  "settlement-pending": "info",
  "withdrawal-pending": "info",
  reconciled: "success",
  "failed-terminal": "danger",
  rejected: "danger",
  cancelled: "neutral",
  "outcome-unknown": "warning",
  Delayed: "warning",
  Blocked: "warning",
  Healthy: "success",
  Degraded: "warning",
  Ready: "success",
  Fresh: "success",
  Stale: "warning",
};

const STATE_LIST = ["happy", "exception", "degraded", "permission", "empty", "readonly"] as const;
type LiqState = (typeof STATE_LIST)[number];

function StateTabs({ active, onChange }: { active: LiqState; onChange: (s: LiqState) => void }) {
  return (
    <div className="mb-5 flex flex-wrap gap-2">
      {STATE_LIST.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-semibold capitalize",
            active === s
              ? "border-primary bg-primary text-on-primary"
              : "border-line bg-surface text-muted hover:border-muted"
          )}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function PermissionBlock({ permission }: { permission: string }) {
  return (
    <div className="rounded-lg border border-warning-border bg-warning-soft p-6 text-center text-sm text-warning">
      <strong>Requires {permission}</strong>
      <p className="mt-2 text-xs text-muted">You can view this screen but cannot modify configuration or take actions.</p>
    </div>
  );
}

function EmptyBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-6 py-12 text-center text-sm text-muted">
      <div className="mb-3 text-3xl opacity-40">&#9678;</div>
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-1 text-xs">{children}</div>
    </div>
  );
}

function ReadonlyBanner() {
  return <InfoBanner variant="info">Read-only mode &mdash; you can view liquidity data but cannot initiate quotes or executions.</InfoBanner>;
}

function DispositionBadge({ label }: { label: string }) {
  return <ToneBadge tone={DISPOSITION_TONE[label] ?? "neutral"}>{label}</ToneBadge>;
}

function VenueStatusBadge({ status }: { status: string }) {
  return <PositionStatus tone={DISPOSITION_TONE[status] ?? "neutral"}>{status}</PositionStatus>;
}

function Panel({ title, bodyClassName, children, actions, className }: { title?: React.ReactNode; bodyClassName?: string; children: React.ReactNode; actions?: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-lg border border-line bg-surface", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
          {title && <div className="text-sm font-bold text-ink">{title}</div>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={bodyClassName ?? "px-5 py-4"}>{children}</div>
    </div>
  );
}

/* ─── LIQ-00 Liquidity Dashboard ─── */
export function LiquidityDashboard() {
  const [state, setState] = React.useState<LiqState>("happy");

  const happy = (
    <>
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard value={LIQ_DASHBOARD_METRICS.connectedVenues} label="Connected Venues" tone="success" hint={LIQ_DASHBOARD_METRICS.disconnectedSub} />
        <StatCard value={LIQ_DASHBOARD_METRICS.venueExposure} label="Venue Exposure" tone="success" hint="Across all venues" />
        <StatCard value={LIQ_DASHBOARD_METRICS.unsettled} label="Unsettled Movements" tone="warning" hint={LIQ_DASHBOARD_METRICS.unsettledSub} />
        <StatCard value={LIQ_DASHBOARD_METRICS.degradedVenues} label="Degraded Venues" tone="warning" hint={LIQ_DASHBOARD_METRICS.degradedSub} />
      </div>
      <Panel title="Venue Balances" bodyClassName="p-0" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">ID</th>
                <th className="px-4 py-2.5">Venue</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Balance</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {LIQ_VENUES.map((v) => (
                <tr key={v.id} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{v.id}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{v.name}</td>
                  <td className="px-4 py-3 text-[13px] text-muted">{v.type}</td>
                  <td className="px-4 py-3"><VenueStatusBadge status={v.status} /></td>
                  <td className="px-4 py-3 text-right text-[13px] font-semibold text-ink">{v.balance}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Link to="/treasury/liquidity/readiness" className="text-xs font-semibold text-primary hover:underline">Readiness</Link>
                      <Link to="/treasury/liquidity/quote" className="text-xs font-semibold text-primary hover:underline">Quote</Link>
                      {v.id === "VEN-001" && <Link to="/treasury/liquidity/incident" className="text-xs font-semibold text-primary hover:underline">Incident</Link>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
      <Panel title="Recent Quotes" bodyClassName="p-0" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Time</th>
                <th className="px-4 py-2.5">Pair</th>
                <th className="px-4 py-2.5">Action</th>
                <th className="px-4 py-2.5">Venue</th>
                <th className="px-4 py-2.5">Price</th>
                <th className="px-4 py-2.5">Disposition</th>
              </tr>
            </thead>
            <tbody>
              {LIQ_RECENT_QUOTES.map((q, i) => (
                <tr key={i} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 text-[13px] text-muted">{q.time}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{q.pair}</td>
                  <td className="px-4 py-3 text-[13px] text-ink">{q.action}</td>
                  <td className="px-4 py-3 text-[13px] text-ink">{q.venue}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold tabular-nums text-ink">{q.price}</td>
                  <td className="px-4 py-3"><DispositionBadge label={q.disposition} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
      <Panel title="Disposition Legend">
        <div className="flex flex-wrap gap-2">
          {LIQ_DISPOSITIONS.map((d) => <DispositionBadge key={d} label={d} />)}
        </div>
      </Panel>
    </>
  );

  const exception = (
    <>
      <InfoBanner variant="danger">
        Quote {LIQ_QUOTE_0891.id} expired before execution. Venue {LIQ_QUOTE_0891.venue} returned timeout after 12s.{" "}
        <DispositionBadge label={LIQ_QUOTE_0891.disposition} />
      </InfoBanner>
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard value={LIQ_DASHBOARD_METRICS.connectedVenues} label="Connected Venues" tone="success" hint="1 error state" />
        <StatCard value="1" label="Failed Orders" tone="danger" hint="failed-terminal" />
        <StatCard value={LIQ_DASHBOARD_METRICS.unsettled} label="Unsettled" tone="warning" hint={LIQ_DASHBOARD_METRICS.unsettledSub} />
        <StatCard value="1" label="Cancelled" tone="default" hint={LIQ_QUOTE_0891.id} />
      </div>
      <Panel title="Failed Operations" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">ID</th>
                <th className="px-4 py-2.5">Pair</th>
                <th className="px-4 py-2.5">Disposition</th>
                <th className="px-4 py-2.5">Error</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{LIQ_QUOTE_0891.id}</td>
                <td className="px-4 py-3 text-[13px] text-ink">{LIQ_QUOTE_0891.pair}</td>
                <td className="px-4 py-3"><DispositionBadge label={LIQ_QUOTE_0891.disposition} /></td>
                <td className="px-4 py-3 text-[13px] text-muted">{LIQ_QUOTE_0891.error}</td>
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
    </>
  );

  const degraded = (
    <>
      <InfoBanner variant="warning">VEN-001 Coinbase Prime: elevated API latency (avg 2.3s). Quote accuracy may be reduced.</InfoBanner>
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard value={LIQ_DASHBOARD_METRICS.connectedVenues} label="Connected Venues" tone="success" hint="1 degraded" />
        <StatCard value={LIQ_DASHBOARD_METRICS.venueExposure} label="Venue Exposure" tone="success" />
      </div>
    </>
  );

  return (
    <div>
      <BackLink href="/treasury">Back to Treasury Home</BackLink>
      <PageHeader title="Liquidity Dashboard" subtitle="Harbor Trust Bank · 2026-09-15 10:45 UTC · Sai Suman Kanuganti (USR-002)" />
      <StateTabs active={state} onChange={setState} />
      {state === "happy" && happy}
      {state === "exception" && exception}
      {state === "degraded" && degraded}
      {state === "permission" && <PermissionBlock permission="treasury:liquidity:read" />}
      {state === "empty" && <EmptyBlock title="No venues connected">Connect an exchange, issuer, or DEX to start trading.</EmptyBlock>}
      {state === "readonly" && (
        <>
          <ReadonlyBanner />
          {happy}
        </>
      )}
      <TreasuryBanner label="LIQ-00: Liquidity Dashboard" />
    </div>
  );
}

/* ─── LIQ-01 Venue Readiness ─── */
export function VenueReadiness() {
  const [state, setState] = React.useState<LiqState>("happy");
  const venue = LIQ_VENUES[0];

  const happy = (
    <Panel title={`${venue.name} — Production Readiness`}>
      <dl className="mb-4 grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted">Venue ID</dt><dd className="font-mono font-semibold text-ink">{venue.id}</dd>
        <dt className="text-muted">Type</dt><dd className="text-ink">{venue.type}</dd>
        <dt className="text-muted">Status</dt><dd><VenueStatusBadge status="Ready" /></dd>
      </dl>
      <div className="divide-y divide-line">
        {venue.readinessChecks?.map((c) => (
          <div key={c.check} className="flex items-center gap-2 py-2 text-sm">
            <span className="grid size-5 place-items-center rounded-full bg-success-soft text-xs font-bold text-success">&#10003;</span>
            <span className="text-ink">{c.check}</span>
          </div>
        ))}
      </div>
    </Panel>
  );

  const exception = (
    <>
      <InfoBanner variant="danger">API key revoked by venue. Re-authenticate to restore access.</InfoBanner>
      <Panel title={`${venue.name} — Blocked`}>
        <div className="divide-y divide-line">
          {[
            { label: "KYB verification complete", pass: true },
            { label: "Account provisioned", pass: true },
            { label: "API permissions", pass: false, extra: <DispositionBadge label="rejected" /> },
            { label: "Funding account linked", pass: true },
            { label: "Sandbox", pass: false, extra: <span className="text-xs text-muted">cannot verify without API</span> },
            { label: "Production", pass: false, extra: <span className="text-xs text-muted">blocked</span> },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-2 py-2 text-sm">
              <span className={cn("grid size-5 place-items-center rounded-full text-xs font-bold", c.pass ? "bg-success-soft text-success" : "bg-danger-soft text-danger")}>
                {c.pass ? "\u2713" : "\u2717"}
              </span>
              <span className="text-ink">{c.label}</span>
              {c.extra && <span className="ml-2">{c.extra}</span>}
            </div>
          ))}
        </div>
      </Panel>
    </>
  );

  const degraded = (
    <>
      <InfoBanner variant="warning">Venue health check returned elevated latency. Readiness status may be stale.</InfoBanner>
      <Panel title={`${venue.name} — Degraded`}>
        <div className="divide-y divide-line">
          {venue.readinessChecks?.map((c, i) => (
            <div key={c.check} className="flex items-center gap-2 py-2 text-sm">
              <span className="grid size-5 place-items-center rounded-full bg-success-soft text-xs font-bold text-success">&#10003;</span>
              <span className="text-ink">{c.check}</span>
              {i === venue.readinessChecks!.length - 1 && <span className="ml-2"><VenueStatusBadge status="Degraded" /></span>}
            </div>
          ))}
        </div>
      </Panel>
    </>
  );

  return (
    <div>
      <BackLink href="/treasury/liquidity">Back to Liquidity Dashboard</BackLink>
      <PageHeader title="Venue Readiness Checklist" subtitle={`${venue.id} ${venue.name} · Marcus Webb (USR-005)`} />
      <StateTabs active={state} onChange={setState} />
      {state === "happy" && happy}
      {state === "exception" && exception}
      {state === "degraded" && degraded}
      {state === "permission" && <PermissionBlock permission="treasury:venue:manage" />}
      {state === "empty" && <EmptyBlock title="No venue selected">Select a venue from the dashboard to view its readiness checklist.</EmptyBlock>}
      {state === "readonly" && <><ReadonlyBanner />{happy}</>}
      <TreasuryBanner label="LIQ-01: Venue Readiness" />
    </div>
  );
}

/* ─── LIQ-02 Quote / RFQ ─── */
export function LiquidityQuote() {
  const [state, setState] = React.useState<LiqState>("happy");
  const [action, setAction] = React.useState("Buy");
  const [pair, setPair] = React.useState("ETH / USDC");
  const [amount, setAmount] = React.useState("100 ETH");

  const happy = (
    <>
      <Panel title="Request for Quote" className="mb-5">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Action" htmlFor="liq-action">
            <Select id="liq-action" value={action} onChange={(e) => setAction(e.target.value)}>
              <option>Buy</option>
              <option>Sell</option>
              <option>Swap</option>
            </Select>
          </Field>
          <Field label="Pair" htmlFor="liq-pair">
            <Select id="liq-pair" value={pair} onChange={(e) => setPair(e.target.value)}>
              <option>ETH / USDC</option>
              <option>BTC / USDC</option>
              <option>USDC / DAI</option>
            </Select>
          </Field>
          <Field label="Amount" htmlFor="liq-amount">
            <Input id="liq-amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
        </div>
      </Panel>
      <Panel title="Route Comparison" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Venue</th>
                <th className="px-4 py-2.5">Price</th>
                <th className="px-4 py-2.5">Slippage</th>
                <th className="px-4 py-2.5">Price Impact</th>
                <th className="px-4 py-2.5">Settlement</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {LIQ_RFQ_ROUTES.map((r) => (
                <tr key={r.venue} className={cn("border-b border-line last:border-b-0", r.best && "bg-success-soft")}>
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{r.venue}</td>
                  <td className={cn("px-4 py-3 text-[13px] font-bold tabular-nums", r.best ? "text-success" : "text-ink")}>{r.price}</td>
                  <td className="px-4 py-3 text-[13px] text-ink">{r.slippage}</td>
                  <td className="px-4 py-3 text-[13px] text-ink">{r.priceImpact}</td>
                  <td className="px-4 py-3 text-[13px] text-ink">{r.settlement}</td>
                  <td className="px-4 py-3">
                    {r.best ? <DispositionBadge label="filled" /> : r.warning ? <DispositionBadge label="outcome-unknown" /> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/treasury/liquidity/intent"><Button size="sm">Accept Best Quote</Button></Link>
          <Button size="sm" variant="secondary" onClick={() => deferTo("Quote Refresh", "Refresh Quotes")}>Refresh Quotes</Button>
        </div>
      </Panel>
    </>
  );

  const exception = (
    <>
      <InfoBanner variant="danger">All venue quotes expired. Market volatility exceeded threshold. <DispositionBadge label="quote-expired" /></InfoBanner>
      <Panel title="Route Comparison">
        <p className="py-4 text-sm text-muted">No active quotes. Retry to fetch fresh prices.</p>
        <Button size="sm" onClick={() => deferTo("Quote Retry", "Retry Quote")}>Retry Quote</Button>
      </Panel>
    </>
  );

  const degraded = (
    <>
      <InfoBanner variant="warning">VEN-001 Coinbase Prime quote may be stale (last update 8s ago). Other venues responding normally.</InfoBanner>
      <Panel title="Route Comparison (Partial)">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Venue</th>
                <th className="px-4 py-2.5">Price</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { venue: "Coinbase Prime", price: "$3,245.50", status: "Stale" },
                { venue: "Circle", price: "$3,247.00", status: "Fresh" },
                { venue: "Uniswap v3", price: "$3,244.80", status: "Fresh" },
              ].map((r) => (
                <tr key={r.venue} className={cn("border-b border-line last:border-b-0", r.status === "Fresh" && "bg-success-soft")}>
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{r.venue}</td>
                  <td className="px-4 py-3 text-[13px] font-bold tabular-nums text-ink">{r.price}</td>
                  <td className="px-4 py-3"><DispositionBadge label={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
    </>
  );

  return (
    <div>
      <BackLink href="/treasury/liquidity">Back to Liquidity Dashboard</BackLink>
      <PageHeader title="Quote / RFQ Flow" subtitle="Harbor Trust Bank · Sai Suman Kanuganti (USR-002)" />
      <StateTabs active={state} onChange={setState} />
      {state === "happy" && happy}
      {state === "exception" && exception}
      {state === "degraded" && degraded}
      {state === "permission" && <PermissionBlock permission="treasury:liquidity:trade" />}
      {state === "empty" && <EmptyBlock title="No quotes requested">Select a pair and amount to request quotes from connected venues.</EmptyBlock>}
      {state === "readonly" && <><ReadonlyBanner />{happy}</>}
      <TreasuryBanner label="LIQ-02: Quote / RFQ" />
    </div>
  );
}

/* ─── LIQ-03 Intent Review ─── */
export function LiquidityIntentReview() {
  const canCreate = usePermission("treasury:create-intent");
  const [state, setState] = React.useState<LiqState>("happy");
  const [confirm, setConfirm] = React.useState("");
  const intent = LIQ_INTENT_REVIEW;

  const happy = (
    <>
      <Panel title="Order Summary" className="mb-5">
        <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">Action</dt><dd className="font-semibold text-ink">{intent.action}</dd>
          <dt className="text-muted">Asset</dt><dd className="font-semibold text-ink">{intent.asset}</dd>
          <dt className="text-muted">Price</dt><dd className="font-semibold text-ink">{intent.price}</dd>
          <dt className="text-muted">Total Cost</dt><dd className="font-bold text-ink">{intent.totalCost}</dd>
          <dt className="text-muted">Venue</dt><dd className="font-semibold text-ink">{intent.venue}</dd>
          <dt className="text-muted">Settlement</dt><dd className="font-semibold text-ink">{intent.settlement}</dd>
          <dt className="text-muted">Initiated by</dt><dd className="font-semibold text-ink">{intent.initiatedBy}</dd>
        </dl>
      </Panel>
      <Panel title="PolicyKit Checks" className="mb-5">
        <div className="divide-y divide-line">
          {intent.policyChecks.map((c) => (
            <div key={c.check} className="flex items-center gap-2 py-2 text-sm">
              <span className="grid size-5 place-items-center rounded-full bg-success-soft text-xs font-bold text-success">&#10003;</span>
              <span className="text-ink">{c.check}</span>
              {c.detail && <span className="text-xs text-muted">&mdash; {c.detail}</span>}
            </div>
          ))}
        </div>
        <InfoBanner variant="warning" className="mt-3">{intent.approvalNote}</InfoBanner>
      </Panel>
      <Panel title="Typed Confirmation">
        <p className="mb-3 text-sm text-ink">Type <strong>{intent.typedConfirm}</strong> to confirm this order:</p>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={intent.typedConfirm} />
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/treasury/liquidity/execution"><Button size="sm" disabled={confirm !== intent.typedConfirm || !canCreate}>Submit for Approval</Button></Link>
          <Button size="sm" variant="secondary" onClick={() => deferTo("Order Cancel", "Cancel")}>Cancel</Button>
        </div>
      </Panel>
    </>
  );

  const exception = (
    <>
      <InfoBanner variant="danger">PolicyKit rejected: velocity limit exceeded. Daily spend $480,000 + $324,550 exceeds $500,000 cap. <DispositionBadge label="rejected" /></InfoBanner>
      <Panel title="Order blocked">
        <p className="py-2 text-sm text-muted">Reduce amount or request a limit increase from your treasury admin.</p>
        <Button size="sm" variant="secondary" onClick={() => deferTo("Order Modify", "Modify Order")}>Modify Order</Button>
      </Panel>
    </>
  );

  return (
    <div>
      <BackLink href="/treasury/liquidity/quote">Back to Quote</BackLink>
      <PageHeader title="Liquidity Intent Review" subtitle="PolicyKit Threshold Check · Leo Nakamura (USR-008)" />
      <StateTabs active={state} onChange={setState} />
      {state === "happy" && happy}
      {state === "exception" && exception}
      {state === "degraded" && <InfoBanner variant="warning">PolicyKit service responding slowly (4.1s). Checks may be delayed.</InfoBanner>}
      {state === "permission" && <PermissionBlock permission={intent.permission} />}
      {state === "empty" && <EmptyBlock title="No pending intent">Accept a quote to generate a liquidity intent for review.</EmptyBlock>}
      {state === "readonly" && (
        <>
          <ReadonlyBanner />
          <Panel title="Order Summary">
            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted">Action</dt><dd className="font-semibold text-ink">Buy 100 ETH</dd>
              <dt className="text-muted">Total</dt><dd className="font-bold text-ink">$324,550.00</dd>
              <dt className="text-muted">Status</dt><dd><DispositionBadge label="order-placed" /></dd>
            </dl>
          </Panel>
        </>
      )}
      <TreasuryBanner label="LIQ-03: Liquidity Intent Review" />
    </div>
  );
}

/* ─── LIQ-04 Execution Monitor ─── */
export function ExecutionMonitor() {
  const [state, setState] = React.useState<LiqState>("happy");
  const order = LIQ_ORDER_7823;

  const timelineDotClass = (d: string) =>
    d === "done" ? "bg-success" : d === "active" ? "bg-primary" : "bg-line";

  const happy = (
    <>
      <Panel title="Execution Timeline" className="mb-5">
        <div className="relative pl-6">
          <div className="absolute bottom-4 left-[11px] top-4 w-0.5 bg-line" />
          <div className="space-y-4">
            {order.timeline.map((step, i) => (
              <div key={i} className="relative">
                <span className={cn("absolute -left-[13px] top-1.5 size-2.5 rounded-full border-2 border-surface", timelineDotClass(step.disposition))} />
                <div className="text-sm font-semibold text-ink">{step.title}</div>
                <div className="text-xs text-muted">
                  {step.ts !== "Pending" ? `${step.ts} · ` : "Pending · "}
                  {step.detail}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Panel>
      <Panel title="Order Details" className="mb-5">
        <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">Order ID</dt><dd className="font-mono font-semibold text-ink">{order.id}</dd>
          <dt className="text-muted">Pair</dt><dd className="font-semibold text-ink">{order.pair}</dd>
          <dt className="text-muted">Side</dt><dd className="font-semibold text-ink">{order.side}</dd>
          <dt className="text-muted">Quantity</dt><dd className="font-semibold text-ink">{order.quantity}</dd>
          <dt className="text-muted">Avg Price</dt><dd className="font-semibold text-ink">{order.avgPrice}</dd>
          <dt className="text-muted">Total</dt><dd className="font-bold text-ink">{order.total}</dd>
          <dt className="text-muted">Venue</dt><dd className="font-semibold text-ink">{order.venue}</dd>
        </dl>
      </Panel>
      <Panel title="Fill Breakdown" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Fill #</th>
                <th className="px-4 py-2.5">Time</th>
                <th className="px-4 py-2.5">Qty</th>
                <th className="px-4 py-2.5">Price</th>
                <th className="px-4 py-2.5">Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {order.fills.map((f) => (
                <tr key={f.num} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{f.num}</td>
                  <td className="px-4 py-3 text-[13px] text-muted">{f.time}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{f.qty}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold tabular-nums text-ink">{f.price}</td>
                  <td className="px-4 py-3 text-[13px] text-ink">{f.cumulative}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
    </>
  );

  const exception = (
    <>
      <InfoBanner variant="danger">Order {order.id} failed: venue rejected withdrawal. Insufficient withdrawal allowance. <DispositionBadge label="failed-terminal" /></InfoBanner>
      <Panel title="Execution Timeline">
        <div className="relative pl-6">
          <div className="absolute bottom-4 left-[11px] top-4 w-0.5 bg-line" />
          {[
            { title: "Quote Accepted", meta: "10:42:03 UTC" },
            { title: "Order Placed", meta: "10:42:05 UTC" },
            { title: "Fill Complete", meta: "10:42:31 UTC · 100 ETH" },
            { title: "Withdrawal Failed", meta: "failed-terminal · Venue error: WITHDRAWAL_LIMIT_EXCEEDED", danger: true },
          ].map((s, i, arr) => (
            <div key={i} className="relative pb-4">
              <span className={cn("absolute -left-[13px] top-1.5 size-2.5 rounded-full border-2 border-surface", i === arr.length - 1 ? "bg-danger" : "bg-success")} />
              <div className={cn("text-sm font-semibold", s.danger ? "text-danger" : "text-ink")}>{s.title}</div>
              <div className="text-xs text-muted">{s.meta}</div>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );

  const degraded = (
    <>
      <InfoBanner variant="warning">Venue status feed delayed. Last update 45s ago. Disposition may be stale.</InfoBanner>
      <Panel title="Execution Timeline">
        <div className="relative pl-6">
          <div className="absolute bottom-4 left-[11px] top-4 w-0.5 bg-line" />
          {[
            { title: "Order Placed", meta: "10:42:05 UTC", done: true },
            { title: "Awaiting fill update…", meta: <DispositionBadge label="outcome-unknown" />, done: false },
          ].map((s, i) => (
            <div key={i} className="relative pb-4">
              <span className={cn("absolute -left-[13px] top-1.5 size-2.5 rounded-full border-2 border-surface", s.done ? "bg-success" : "bg-primary")} />
              <div className="text-sm font-semibold text-ink">{s.title}</div>
              <div className="text-xs text-muted">{s.meta}</div>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );

  return (
    <div>
      <BackLink href="/treasury/liquidity/intent">Back to Intent Review</BackLink>
      <PageHeader title="Execution Monitor" subtitle={`Order ${order.id} · Sai Suman Kanuganti (USR-002)`} />
      <StateTabs active={state} onChange={setState} />
      {state === "happy" && happy}
      {state === "exception" && exception}
      {state === "degraded" && degraded}
      {state === "permission" && <PermissionBlock permission="treasury:execution:read" />}
      {state === "empty" && <EmptyBlock title="No active executions">Submit an order to monitor its execution lifecycle.</EmptyBlock>}
      {state === "readonly" && (
        <>
          <ReadonlyBanner />
          <Panel title={`Order ${order.id}`}>
            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted">Status</dt><dd><DispositionBadge label={order.disposition} /></dd>
              <dt className="text-muted">Fill</dt><dd className="font-semibold text-ink">{order.filled}</dd>
            </dl>
          </Panel>
        </>
      )}
      <TreasuryBanner label="LIQ-04: Execution Monitor" />
    </div>
  );
}

/* ─── LIQ-05 Venue Incident ─── */
export function VenueIncident() {
  const [state, setState] = React.useState<LiqState>("happy");
  const venue = LIQ_VENUES[0];
  const incident = venue.incident!;

  const happy = (
    <>
      <InfoBanner variant="warning">{venue.name} &mdash; API Degraded</InfoBanner>
      <Panel title="Incident Details" className="mb-5">
        <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">Venue</dt><dd className="font-semibold text-ink">{venue.name} ({venue.id})</dd>
          <dt className="text-muted">Issue</dt><dd className="font-semibold text-ink">{incident.issue}</dd>
          <dt className="text-muted">Detected</dt><dd className="font-semibold text-ink">{incident.detected}</dd>
          <dt className="text-muted">Duration</dt><dd className="font-semibold text-ink">{incident.duration}</dd>
          <dt className="text-muted">Severity</dt><dd><VenueStatusBadge status="Degraded" /></dd>
        </dl>
      </Panel>
      <Panel title="Affected Operations" bodyClassName="p-0" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">ID</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Impact</th>
              </tr>
            </thead>
            <tbody>
              {incident.affected.map((a) => (
                <tr key={a.id} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 text-[13px] text-ink">{a.type}</td>
                  <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{a.id}</td>
                  <td className="px-4 py-3"><DispositionBadge label={a.type.includes("Order") ? "Delayed" : "Blocked"} /></td>
                  <td className="px-4 py-3 text-[13px] text-muted">{a.impact}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>
      <Panel title="Actions">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => deferTo("Retry Operations", "Retry Pending Operations")}>Retry Pending Operations</Button>
          <Button size="sm" variant="secondary" onClick={() => deferTo("Reroute", "Reroute to Circle (VEN-002)")}>Reroute to Circle (VEN-002)</Button>
          <Button size="sm" variant="secondary" onClick={() => deferTo("Escalate", "Escalate to Ops")}>Escalate to Ops</Button>
          <Button size="sm" variant="destructive" onClick={() => deferTo("Cancel Orders", "Cancel Affected Orders")}>Cancel Affected Orders</Button>
        </div>
      </Panel>
    </>
  );

  const exception = (
    <>
      <InfoBanner variant="danger">{venue.name} &mdash; Venue Unavailable. API key revoked. All operations halted. <DispositionBadge label="failed-terminal" /></InfoBanner>
      <Panel title="Critical Incident">
        <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">Issue</dt><dd className="font-semibold text-ink">API key revoked by venue administrator</dd>
          <dt className="text-muted">Affected</dt><dd className="font-semibold text-ink">3 pending orders, 1 unsettled withdrawal</dd>
          <dt className="text-muted">Exposure</dt><dd className="font-bold text-ink">{venue.balance}</dd>
        </dl>
        <div className="mt-4">
          <Button size="sm" variant="destructive" onClick={() => deferTo("Emergency Pause", "Emergency: Pause All Venue Operations")}>Emergency: Pause All Venue Operations</Button>
        </div>
      </Panel>
    </>
  );

  return (
    <div>
      <BackLink href="/treasury/liquidity">Back to Liquidity Dashboard</BackLink>
      <PageHeader title="Venue Incident" subtitle={`${venue.id} ${venue.name} · Marcus Webb (USR-005)`} />
      <StateTabs active={state} onChange={setState} />
      {state === "happy" && happy}
      {state === "exception" && exception}
      {state === "degraded" && (
        <>
          <InfoBanner variant="warning">Incident monitoring service itself is degraded. Incident data may be delayed by up to 60s.</InfoBanner>
          <Panel title={`${venue.id} Incident (stale data)`}>
            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted">Last refresh</dt><dd className="font-semibold text-ink">60s ago</dd>
              <dt className="text-muted">Status</dt><dd><VenueStatusBadge status="Degraded" /></dd>
            </dl>
          </Panel>
        </>
      )}
      {state === "permission" && <PermissionBlock permission="treasury:incident:manage" />}
      {state === "empty" && <EmptyBlock title="No active incidents">All connected venues are operating normally.</EmptyBlock>}
      {state === "readonly" && (
        <>
          <ReadonlyBanner />
          <Panel title={`${venue.name}`}>
            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted">Status</dt><dd><VenueStatusBadge status="Degraded" /></dd>
              <dt className="text-muted">Duration</dt><dd className="font-semibold text-ink">{incident.duration}</dd>
            </dl>
          </Panel>
        </>
      )}
      <TreasuryBanner label="LIQ-05: Venue Incident" />
    </div>
  );
}
